import { getAuthToken, SERVER_URL } from '../libs/auth'

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async fetch(path: string, options: RequestInit = {}): Promise<Response> {
    const token = getAuthToken()
    const headers = new Headers(options.headers)
    if (token)
      headers.set('Authorization', `Bearer ${token}`)
    if (!headers.has('Content-Type') && options.body)
      headers.set('Content-Type', 'application/json')

    return fetch(`${this.baseUrl}${path}`, { ...options, headers })
  }

  private async json<T = any>(path: string, options: RequestInit = {}): Promise<T> {
    const res = await this.fetch(path, options)
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: res.statusText }))
      throw new Error(error.message || `Request failed: ${res.status}`)
    }
    return res.json()
  }

  // Characters
  async getCharacters(all = false) {
    return this.json(`/api/characters/?all=${all}`)
  }

  async getCharacter(id: string) {
    return this.json(`/api/characters/${id}`)
  }

  async createCharacter(payload: any) {
    return this.json('/api/characters/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async updateCharacter(id: string, payload: any) {
    return this.json(`/api/characters/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  async deleteCharacter(id: string) {
    await this.fetch(`/api/characters/${id}`, { method: 'DELETE' })
  }

  async likeCharacter(id: string) {
    return this.json(`/api/characters/${id}/like`, { method: 'POST' })
  }

  async bookmarkCharacter(id: string) {
    return this.json(`/api/characters/${id}/bookmark`, { method: 'POST' })
  }

  // Providers
  async getProviders() {
    return this.json('/api/providers/')
  }

  async getProvider(id: string) {
    return this.json(`/api/providers/${id}`)
  }

  async createProvider(payload: any) {
    return this.json('/api/providers/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async updateProvider(id: string, payload: any) {
    return this.json(`/api/providers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  async deleteProvider(id: string) {
    await this.fetch(`/api/providers/${id}`, { method: 'DELETE' })
  }

  // Chats
  async getChats() {
    return this.json('/api/chats/')
  }

  async getChat(id: string) {
    return this.json(`/api/chats/${id}`)
  }

  async createChat(payload: any) {
    return this.json('/api/chats/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async getChatMessages(id: string, limit = 50, offset = 0) {
    return this.json(`/api/chats/${id}/messages?limit=${limit}&offset=${offset}`)
  }

  async addChatMessage(chatId: string, payload: any) {
    return this.json(`/api/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async updateChat(id: string, payload: any) {
    return this.json(`/api/chats/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  async deleteChat(id: string) {
    await this.fetch(`/api/chats/${id}`, { method: 'DELETE' })
  }

  async syncChat(payload: any) {
    return this.json('/api/chats/sync', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  // Search
  async unifiedSearch(payload: any) {
    return this.json('/api/search/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async searchMessages(payload: any) {
    return this.json('/api/search/messages', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async searchMemories(payload: any) {
    return this.json('/api/search/memories', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  // Memories
  async getMemories(query: string, size = 20) {
    return this.json(`/api/memories/?q=${encodeURIComponent(query)}&size=${size}`)
  }

  async createMemory(payload: any) {
    return this.json('/api/memories/', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async updateMemory(id: string, payload: any) {
    return this.json(`/api/memories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  async deleteMemory(id: string) {
    return this.json(`/api/memories/${id}`, { method: 'DELETE' })
  }

  async searchMemoryFragments(payload: any) {
    return this.json('/api/memories/search', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  // Market
  async getStockMinute(code = '000001.SH') {
    return this.json(`/api/market/stock-minute?code=${encodeURIComponent(code)}`)
  }

  async getNews(size = 20) {
    return this.json(`/api/market/news?size=${size}`)
  }

  // Live Room
  async getLiveRoomHistory() {
    return this.json<{ data: any[] }>('/api/live-room/history')
  }
}

export const client = new ApiClient(SERVER_URL)
